import type { FC } from 'react';
import clsx from 'clsx';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { MLToolSummary } from '../type/MLTypes';

interface MLToolListProps {
  tools: MLToolSummary[];
  selectedToolId: string | null;
  onSelect: (toolId: string) => void;
  disabled: boolean;
}

function formatLastUsed(dateStr: string | null, t: (key: string) => string): string {
  if (!dateStr) return t('mlRul.tool.lastUsedNever');
  const date = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateStr));
  return t('mlRul.tool.lastUsed').replace('{{date}}', date);
}

function statusTone(status: MLToolSummary['status']): BadgeTone {
  if (status === 'scrapped') return 'error';
  if (status === 'in-use') return 'primary';
  return 'neutral';
}

function statusLabel(status: MLToolSummary['status'], t: (key: string) => string): string {
  if (status === 'scrapped') return t('mlRul.tool.statusScrapped');
  if (status === 'in-use') return t('mlRul.tool.statusInUse');
  return t('mlRul.tool.statusIdle');
}

export const MLToolList: FC<MLToolListProps> = ({ tools, selectedToolId, onSelect, disabled }) => {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  if (tools.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('mlRul.context.step3NoTools')}</p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {tools.map((tool) => {
        const isSelected = tool.toolId === selectedToolId;
        return (
          <li key={tool.toolId}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onSelect(tool.toolId)}
              className={clsx(
                'w-full rounded-xl border bg-card/60 px-3 py-2.5 text-left text-sm transition-all duration-150',
                'hover:border-primary/30 hover:shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                'disabled:pointer-events-none disabled:opacity-50',
                isSelected && 'border-primary/40 bg-primary/5',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {(() => {
                    const resolved = resolve(tool.reference ?? '');
                    return (
                      <>
                        <p className="truncate font-medium text-foreground">
                          {resolved.label || t('mlRul.tool.unknownReference')}
                        </p>
                        {resolved.hasAlias && (
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {resolved.canonical}
                          </p>
                        )}
                      </>
                    );
                  })()}
                  <p className="font-mono text-xs text-muted-foreground">{tool.toolId}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatLastUsed(tool.lastUsedAt, t)}
                  </p>
                </div>
                <Badge tone={statusTone(tool.status)}>{statusLabel(tool.status, t)}</Badge>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
